import { useMockStore } from "./store";
import { mockProjectClientContacts } from "./seed";
import type { DataAPI } from "../types";
import { DEFAULT_TAB_PERMISSIONS } from "../types";
import { buildAdminInsights } from "../insights";
import {
  applyClientFilter,
  applyProjectFilter,
  applyInvoiceFilter,
  applyQuoteFilter,
} from "@/os/settings/dataFilterApply";
import { getDataFilter, isInYear } from "@/os/settings/useDataFilterStore";

const s = () => useMockStore.getState();
const id = () => crypto.randomUUID();
const now = () => new Date().toISOString();
const wait = <T>(v: T) => Promise.resolve(v);

const companyFor = (clientId: string | null | undefined) =>
  clientId ? s().clients.find((c) => c.id === clientId)?.company_name ?? null : null;

/** Active project_team rows joined with team_profiles, capped at 6, ordered. */
function teamMembersFor(projectId: string) {
  const st = s();
  return st.projectTeam
    .filter((pt) => pt.project_id === projectId)
    .sort((a, b) => a.order_index - b.order_index)
    .slice(0, 6)
    .map((pt) => {
      const tp = st.teamProfiles.find((p: any) => p.id === pt.team_profile_id);
      return {
        id: pt.team_profile_id,
        display_name: tp?.display_name ?? "—",
        avatar_url: tp?.avatar_url ?? null,
        role_on_project: pt.role_on_project ?? null,
      };
    });
}

export const mockApi: DataAPI = {
  async adminDashboardStats() {
    const st = s();
    // Apply global filters so dashboard KPIs respect simulated-clients +
    // active-year settings.
    const clients = applyClientFilter(st.clients, { projects: st.projects, invoices: st.invoices, quotes: st.quotes });
    const projects = applyProjectFilter(st.projects, st.clients);
    const invoices = applyInvoiceFilter(st.invoices, st.clients);
    const today = new Date();
    const openInvoices = invoices.filter((i) => i.status !== "paid" && i.status !== "void").length;
    const overdue = invoices.filter(
      (i) => i.status !== "paid" && i.status !== "void" && i.due_date && new Date(i.due_date) < today,
    ).length;
    const revenue = invoices.filter((i) => i.status === "paid").reduce((sum, i) => sum + Number(i.amount || 0), 0);
    return wait({
      clients: clients.length,
      activeProjects: projects.filter((p) => p.status !== "archived").length,
      openInvoices,
      overdue,
      revenue,
    });
  },

  // Clients
  listClients() {
    const st = s();
    const filtered = applyClientFilter(st.clients, { projects: st.projects, invoices: st.invoices, quotes: st.quotes });
    return wait([...filtered].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((c) => {
      const ps = st.projects.filter((p) => p.client_id === c.id);
      const is = st.invoices.filter((i) => i.client_id === c.id);
      const billed_total = is.filter((i) => i.status === "paid").reduce((s, i) => s + Number(i.amount ?? 0), 0);
      const outstanding = is.filter((i) => i.status !== "paid" && i.status !== "void").reduce((s, i) => s + Number(i.amount ?? 0), 0);
      const times = [c.created_at, ...is.map((i) => i.paid_at ?? i.created_at)].filter(Boolean) as string[];
      const last_activity = times.length ? times.sort().slice(-1)[0] : c.created_at;
      return { ...c, projects_count: ps.filter((p) => p.status !== "archived").length, billed_total, outstanding, last_activity };
    }));
  },
  listClientsLite() {
    const st = s();
    return wait(applyClientFilter(st.clients).map((c) => ({ id: c.id, company_name: c.company_name, client_type: (c as any).client_type })));
  },
  async getClient(cid) {
    const st = s();
    const client = st.clients.find((c) => c.id === cid) ?? null;
    const projects = st.projects.filter((p) => p.client_id === cid).map((p) => ({ id: p.id, name: p.name, status: p.status, progress: p.progress }));
    const invoices = st.invoices.filter((i) => i.client_id === cid).map((i) => ({ id: i.id, invoice_number: i.invoice_number, amount: i.amount, currency: i.currency, status: i.status, due_date: i.due_date }));
    return wait({ client, projects, invoices });
  },
  async createClient(p) {
    const newId = id();
    useMockStore.setState((st) => ({
      clients: [{
        id: newId,
        company_name: p.company_name ?? "",
        contact_name: p.contact_name ?? null,
        contact_email: p.contact_email ?? null,
        contact_phone: (p as any).contact_phone ?? null,
        status: p.status ?? "prospect",
        notes: p.notes ?? null,
        preferred_language: (p as any).preferred_language ?? "es",
        default_payment_method_id: (p as any).default_payment_method_id ?? null,
        timezone: (p as any).timezone ?? null,
        user_id: null,
        created_at: now(),
      }, ...st.clients],
    }));
    return newId;
  },
  async updateClient(cid, patch) {
    useMockStore.setState((st) => ({
      clients: st.clients.map((c) => (c.id === cid ? { ...c, ...patch } : c)),
    }));
  },

  async getClientImpact(cid) {
    const st = s();
    const projects = st.projects.filter((p) => p.client_id === cid);
    const activeProjects = projects.filter((p: any) => p.status !== "archived").length;
    const openQuotes = st.quotes.filter(
      (q: any) => q.client_id === cid && ["draft", "sent", "accepted"].includes(q.status)
    ).length;
    const clientInvoices = st.invoices.filter((i: any) => i.client_id === cid);
    const outstandingAmount = clientInvoices
      .filter((i: any) => i.status !== "paid" && i.status !== "void")
      .reduce((sum: number, i: any) => sum + Number(i.amount ?? 0), 0);
    const invoiceIds = new Set(clientInvoices.map((i: any) => i.id));
    const pendingPaymentsCount = (st.payments ?? [])
      .filter((p: any) => invoiceIds.has(p.invoice_id) && p.status === "declared").length;
    const client = st.clients.find((c) => c.id === cid);
    return wait({
      activeProjects,
      openQuotes,
      outstandingAmount,
      pendingPaymentsCount,
      currency: (client as any)?.currency ?? "USD",
    });
  },

  async setClientActive(cid, active) {
    useMockStore.setState((st) => {
      const nextStatus = active ? "active" : "inactive";
      const clients = st.clients.map((c) =>
        c.id === cid ? { ...c, status: nextStatus as any } : c
      );
      if (active) return { clients };
      // Deactivate cascade.
      const projects = st.projects.map((p: any) => {
        if (p.client_id !== cid) return p;
        return {
          ...p,
          status: p.status === "archived" ? p.status : "archived",
          billing: {
            ...(p.billing ?? {}),
            paused_at: new Date().toISOString(),
            paused_reason: "client_inactive",
          },
        };
      });
      const quotes = st.quotes.map((q: any) => {
        if (q.client_id !== cid) return q;
        if (["draft", "sent", "accepted"].includes(q.status)) {
          return { ...q, status: "rejected" };
        }
        return q;
      });
      return { clients, projects, quotes };
    });
    return wait(undefined as any);
  },

  // Projects
  listProjects() {
    const st = s();
    const filtered = applyProjectFilter(st.projects, st.clients);
    return wait([...filtered].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((p) => {
      const milestones = st.milestones.filter((m) => m.project_id === p.id);
      const tasks = st.tasks.filter((t) => t.project_id === p.id);
      const members = st.members.filter((m) => m.project_id === p.id);
      const updates = st.updates.filter((u) => u.project_id === p.id).map((u) => u.created_at).sort();
      const team = teamMembersFor(p.id);
      return {
        ...p,
        clients: p.client_id ? { company_name: companyFor(p.client_id) ?? "" } : null,
        team_count: Math.max(members.length, team.length),
        team_members: team,
        open_tasks: tasks.filter((t) => t.status !== "done" && (t as any).status !== "cancelled").length,
        milestones_total: milestones.length,
        milestones_done: milestones.filter((m) => m.status === "completed" || (m as any).status === "done").length,
        last_update: updates.length ? updates[updates.length - 1] : null,
      };
    }));
  },
  async createProject(p) {
    const st = s();
    const snap = (p as any).resources_discount_percent_snapshot
      ?? Number((st.appSettings as any)?.resources_discount_percent ?? 0);
    const newP: any = {
      id: id(),
      name: p.name ?? "",
      description: p.description ?? null,
      client_id: p.client_id ?? null,
      status: p.status ?? "discovery",
      progress: 0,
      budget: p.budget ?? null,
      currency: p.currency ?? "USD",
      resources_discount_percent_snapshot: snap,
      billing: (p as any).billing ?? {},
      ai_settings: (p as any).ai_settings ?? {},
      created_at: now(),
    };
    newP.clients = newP.client_id ? { company_name: companyFor(newP.client_id) ?? "" } : null;
    useMockStore.setState((st) => ({ projects: [newP, ...st.projects] }));
  },
  async getProjectDetail(pid) {
    const st = s();
    const proj = st.projects.find((p) => p.id === pid) ?? null;
    const project = proj ? { ...proj, clients: proj.client_id ? { company_name: companyFor(proj.client_id) ?? "" } : null } : null;
    const milestones = st.milestones.filter((m) => m.project_id === pid).sort((a, b) => a.order_index - b.order_index);
    const tasks = st.tasks.filter((t) => t.project_id === pid);
    const updates = st.updates.filter((u) => u.project_id === pid).sort((a, b) => b.created_at.localeCompare(a.created_at));
    const members = st.members.filter((m) => m.project_id === pid).map((m) => {
      const u = st.users.find((x) => x.id === m.user_id);
      return { ...m, profiles: u ? { full_name: u.full_name, email: u.email } : null };
    });
    return wait({ project, milestones, tasks, members, updates });
  },
  async updateProject(pid, patch) {
    useMockStore.setState((st) => ({
      projects: st.projects.map((p) => (p.id === pid ? { ...p, ...patch } : p)),
    }));
  },
  async getProjectAISettings(pid) {
    const st = s();
    const proj = st.projects.find((p) => p.id === pid) ?? null;
    const project = proj
      ? { id: proj.id, name: proj.name, status: proj.status, ai_settings: (proj as any).ai_settings ?? {}, clients: proj.client_id ? { company_name: companyFor(proj.client_id) ?? "" } : null }
      : null;
    return wait({ project } as any);
  },
  async updateProjectAISettings(pid, settings) {
    useMockStore.setState((st) => ({
      projects: st.projects.map((p) => (p.id === pid ? { ...p, ai_settings: settings ?? {} } : p)),
    }));
  },
  async createMilestone(p) {
    useMockStore.setState((st) => ({
      milestones: [...st.milestones, {
        id: id(), project_id: p.project_id, title: p.title, description: p.description ?? null,
        status: "pending", order_index: st.milestones.filter((m) => m.project_id === p.project_id).length, created_at: now(),
        start_date: p.start_date ?? null,
        end_date: p.end_date ?? p.due_date ?? null,
        due_date: p.due_date ?? p.end_date ?? null,
      }],
    }));
  },
  async createTask(p) {
    useMockStore.setState((st) => ({
      tasks: [
        ...st.tasks,
        {
          id: id(),
          project_id: p.project_id,
          title: p.title,
          description: p.description ?? null,
          // Matches DB enum task_status default ('todo')
          status: "todo",
          created_at: now(),
        },
      ],
    }));
  },
  async createProjectUpdate(p) {
    useMockStore.setState((st) => ({
      updates: [{ id: id(), project_id: p.project_id, title: p.title, body: p.body ?? null, created_at: now() }, ...st.updates],
    }));
  },
  async listOperatorsForAssign() {
    return wait(s().users.filter((u) => u.roles.includes("operator")).map((u) => ({
      user_id: u.id, profiles: { full_name: u.full_name, email: u.email },
    })));
  },
  async assignProjectMember(project_id, user_id) {
    useMockStore.setState((st) => ({
      // Matches DB default on project_members.role ('operator') and unique (project_id, user_id)
      members: [
        ...st.members.filter((m) => !(m.project_id === project_id && m.user_id === user_id)),
        { project_id, user_id, role: "operator" },
      ],
    }));
  },

  // Invoices
  listInvoices() {
    const st = s();
    const filtered = applyInvoiceFilter(st.invoices, st.clients);
    return wait([...filtered].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((i) => ({
      ...i, clients: i.client_id ? { company_name: companyFor(i.client_id) ?? "" } : null,
    })));
  },
  async createInvoice(p) {
    const newI: any = {
      id: id(),
      client_id: p.client_id ?? null,
      invoice_number: p.invoice_number ?? `INV-${Date.now()}`,
      amount: p.amount ?? 0,
      currency: p.currency ?? "USD",
      status: p.status ?? "draft",
      issue_date: now(),
      due_date: p.due_date ?? null,
      paid_at: null,
      notes: (p as any).notes ?? null,
      payment_method_id: (p as any).payment_method_id ?? null,
      reminder_cadence: (p as any).reminder_cadence ?? "weekly",
      attachment_url: (p as any).attachment_url ?? null,
      attachment_path: (p as any).attachment_path ?? null,
      attachment_name: (p as any).attachment_name ?? null,
      attachment_mime: (p as any).attachment_mime ?? null,
      created_at: now(),
    };
    newI.clients = newI.client_id ? { company_name: companyFor(newI.client_id) ?? "" } : null;
    useMockStore.setState((st) => ({ invoices: [newI, ...st.invoices] }));
    return newI.id as string;
  },
  async markInvoicePaid(iid) {
    useMockStore.setState((st) => ({
      invoices: st.invoices.map((i) => (i.id === iid ? { ...i, status: "paid", paid_at: now() } : i)),
    }));
  },
  async updateInvoice(iid, patch) {
    useMockStore.setState((st) => ({
      invoices: st.invoices.map((i) => (i.id === iid ? { ...i, ...patch } as any : i)),
    }));
  },
  async deleteInvoice(iid) {
    useMockStore.setState((st) => ({
      invoices: st.invoices.filter((i) => i.id !== iid),
      payments: st.payments.filter((p) => p.invoice_id !== iid),
    }));
  },
  async listInvoicePayments(invoiceId) {
    return wait(s().payments.filter((p) => p.invoice_id === invoiceId).sort((a, b) => b.paid_at.localeCompare(a.paid_at)) as any);
  },
  async updatePayment(pid, patch) {
    useMockStore.setState((st) => ({
      payments: st.payments.map((p) => (p.id === pid ? { ...p, ...patch } as any : p)),
    }));
  },
  async deletePayment(pid) {
    useMockStore.setState((st) => ({ payments: st.payments.filter((p) => p.id !== pid) }));
  },
  async listInvoiceReminders(invoiceId) {
    const all = (useMockStore.getState() as any).invoiceReminders ?? [];
    return wait(all.filter((r: any) => r.invoice_id === invoiceId).sort((a: any, b: any) => b.sent_at.localeCompare(a.sent_at)) as any);
  },
  async createInvoiceReminder(p) {
    useMockStore.setState((st: any) => {
      const list = st.invoiceReminders ?? [];
      const row = {
        id: crypto.randomUUID(),
        invoice_id: p.invoice_id,
        sent_at: p.sent_at ?? new Date().toISOString(),
        kind: p.kind ?? "manual",
        cadence: p.cadence ?? null,
        outstanding_amount: p.outstanding_amount ?? 0,
        currency: p.currency ?? "USD",
        channel: p.channel ?? "email",
        notes: p.notes ?? null,
        created_at: new Date().toISOString(),
      };
      return {
        invoiceReminders: [row, ...list],
        invoices: st.invoices.map((i: any) => (i.id === p.invoice_id ? { ...i, last_reminder_at: row.sent_at } : i)),
      } as any;
    });
  },

  // Quotes
  listQuotes() {
    const st = s();
    const filtered = applyQuoteFilter(st.quotes, st.clients);
    return wait([...filtered].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((q) => ({
      ...q, clients: q.client_id ? { company_name: companyFor(q.client_id) ?? "" } : null,
    })));
  },
  async createQuote(p) {
    const newQ: any = {
      id: id(),
      title: p.title ?? "",
      client_id: p.client_id ?? null,
      prospect_name: p.prospect_name ?? null,
      prospect_email: p.prospect_email ?? null,
      status: p.status ?? "draft",
      currency: p.currency ?? "USD",
      notes: p.notes ?? null,
      valid_until: p.valid_until ?? null,
      line_items: p.line_items ?? [],
      subtotal: p.subtotal ?? 0,
      tax: p.tax ?? 0,
      total: p.total ?? 0,
      created_at: now(),
    };
    newQ.clients = newQ.client_id ? { company_name: companyFor(newQ.client_id) ?? "" } : null;
    useMockStore.setState((st) => ({ quotes: [newQ, ...st.quotes] }));
    return newQ.id as string;
  },
  async updateQuote(qid, patch) {
    useMockStore.setState((st) => {
      const FROZEN = new Set(["sent", "accepted"]);
      return {
        quotes: st.quotes.map((q) => {
          if (q.id !== qid) return q;
          const next: any = { ...q, ...patch };
          if (
            (patch as any)?.status &&
            FROZEN.has((patch as any).status) &&
            !(q as any).discount_snapshot
          ) {
            const ai = (next.ai_payload ?? (q as any).ai_payload) as any;
            next.discount_snapshot = {
              percent: Number((st.appSettings as any)?.resources_discount_percent ?? 0),
              per_quote_percent: Number(ai?.discount?.percent ?? 0),
              captured_at: now(),
              on_status: (patch as any).status,
            };
          }
          return next;
        }),
      };
    });
  },

  async deleteQuote(qid) {
    useMockStore.setState((st) => ({
      quotes: st.quotes.filter((q) => q.id !== qid),
    }));
  },

  async getQuoteDetail(qid) {
    const st = s();
    const q = st.quotes.find((x) => x.id === qid) ?? null;
    let quote: any = null;
    if (q) {
      const client = q.client_id ? st.clients.find((c) => c.id === q.client_id) : null;
      quote = {
        ...q,
        clients: client
          ? { company_name: client.company_name, contact_name: client.contact_name, contact_email: client.contact_email }
          : null,
      };
    }
    // Resolve each quote_resource against the live resources catalog so prices
    // stay coherent with Resources > Configuration (no stale overrides).
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const findCatalogId = (label: string): string | null => {
      const target = norm(label);
      if (!target) return null;
      // 1) exact normalized headline match
      let hit = st.resources.find((r) => norm(r.headline) === target);
      if (hit) return hit.id;
      // 2) startsWith or contains match (e.g. "Senior PM" → "Senior Project Manager")
      hit = st.resources.find((r) => {
        const h = norm(r.headline);
        return h.startsWith(target) || target.startsWith(h);
      });
      if (hit) return hit.id;
      hit = st.resources.find((r) => norm(r.headline).includes(target) || target.includes(norm(r.headline)));
      return hit?.id ?? null;
    };
    const resources = st.quoteResources
      .filter((r) => r.quote_id === qid)
      .sort((a, b) => a.order_index - b.order_index)
      .map((r) => {
        const rid = r.resource_id ?? findCatalogId(r.role_label || "");
        // Drop unit_price_override so the catalog price drives the math.
        return { ...r, resource_id: rid, unit_price_override: null };
      }) as any;
    const team = st.quoteTeam
      .filter((m) => m.quote_id === qid)
      .sort((a, b) => a.order_index - b.order_index)
      .map((m) => {
        const p = st.teamProfiles.find((tp) => tp.id === m.team_profile_id) ?? null;
        return {
          ...m,
          team_profiles: p
            ? { id: p.id, display_name: p.display_name, headline: p.headline, avatar_url: p.avatar_url, expertise: (p as any).expertise ?? [] }
            : null,
        };
      }) as any;
    return wait({ quote, resources, team });
  },

  // Payments
  listPayments() {
    const st = s();
    const sim = new Set(st.clients.filter((c: any) => c.is_simulated).map((c) => c.id));
    const invToClient = new Map<string, string | null>(st.invoices.map((i: any) => [i.id, i.client_id ?? null]));
    const { showSimulated, activeYear } = getDataFilter();
    let rows = [...st.payments];
    if (!showSimulated) {
      rows = rows.filter((p: any) => {
        const cid = p.invoice_id ? invToClient.get(p.invoice_id) : null;
        return !cid || !sim.has(cid);
      });
    }
    if (activeYear !== "all") {
      rows = rows.filter((p: any) => isInYear(p.paid_at, activeYear));
    }
    return wait(rows.sort((a, b) => b.paid_at.localeCompare(a.paid_at)));
  },
  async createPayment(p) {
    const newP: any = {
      id: id(),
      invoice_id: p.invoice_id ?? null,
      amount: p.amount ?? 0,
      currency: p.currency ?? "USD",
      method: p.method ?? null,
      reference: (p as any).reference ?? null,
      notes: (p as any).notes ?? null,
      paid_at: (p as any).paid_at ?? now(),
      receipt_url: (p as any).receipt_url ?? null,
      receipt_path: (p as any).receipt_path ?? null,
      receipt_name: (p as any).receipt_name ?? null,
      receipt_mime: (p as any).receipt_mime ?? null,
      status: (p as any).status ?? "accepted",
      declared_by: (p as any).declared_by ?? null,
      declared_by_name: (p as any).declared_by_name ?? null,
    };
    useMockStore.setState((st) => ({ payments: [newP, ...st.payments] }));
  },

  // Users
  listUsersWithRoles() {
    return wait(s().users.map((u) => ({ ...u, roles: [...u.roles] })));
  },
  async addUserRole(uid, role) {
    useMockStore.setState((st) => ({
      users: st.users.map((u) => (u.id === uid && !u.roles.includes(role) ? { ...u, roles: [...u.roles, role] } : u)),
    }));
  },
  async removeUserRole(uid, role) {
    useMockStore.setState((st) => ({
      users: st.users.map((u) => (u.id === uid ? { ...u, roles: u.roles.filter((r) => r !== role) } : u)),
    }));
  },

  // Client hub — in sandbox we surface the first "active" client's data
  async clientHubData() {
    const st = s();
    // Sandbox persona for the Client Hub is Tom Becker @ Orbit PM, so
    // prefer that client; fall back to the first active client.
    const cl =
      st.clients.find((c) => c.company_name === "Orbit PM") ??
      st.clients.find((c) => c.status === "active");
    if (!cl) return wait({ projects: [], invoices: [] });
    return wait({
      projects: st.projects.filter((p) => p.client_id === cl.id).map((p) => {
        const milestones = st.milestones.filter((m) => m.project_id === p.id);
        const tasks = st.tasks.filter((t) => t.project_id === p.id);
        const updates = st.updates.filter((u) => u.project_id === p.id).map((u) => u.created_at).sort();
        const team = teamMembersFor(p.id);
        return {
          id: p.id, name: p.name, description: (p as any).description ?? null,
          status: p.status, progress: p.progress,
          due_date: p.due_date, start_date: p.start_date,
          created_at: p.created_at, updated_at: (p as any).updated_at ?? p.created_at,
          currency: p.currency, budget: p.budget, client_id: p.client_id,
          clients: { company_name: companyFor(p.client_id) ?? "" },
          team_count: team.length,
          team_members: team,
          open_tasks: tasks.filter((t) => t.status !== "done" && (t as any).status !== "cancelled").length,
          milestones_total: milestones.length,
          milestones_done: milestones.filter((m) => m.status === "completed" || (m as any).status === "done").length,
          last_update: updates.length ? updates[updates.length - 1] : ((p as any).updated_at ?? p.created_at),
        };
      }),
      invoices: st.invoices.filter((i) => i.client_id === cl.id),
    });
  },
  async clientInvoices() {
    const st = s();
    const cl =
      st.clients.find((c) => c.company_name === "Orbit PM") ??
      st.clients.find((c) => c.status === "active");
    if (!cl) return wait([]);
    return wait(st.invoices.filter((i) => i.client_id === cl.id));
  },
  async clientProjectDetail(pid) {
    const st = s();
    const proj = st.projects.find((p) => p.id === pid) ?? null;
    const projWithClient = proj
      ? { ...proj, clients: proj.client_id ? { company_name: companyFor(proj.client_id) ?? "" } : null }
      : null;
    const quote = proj?.client_id
      ? [...st.quotes]
          .filter((q) => q.client_id === proj.client_id)
          .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
      : null;
    return wait({
      project: projWithClient,
      milestones: st.milestones.filter((m) => m.project_id === pid).sort((a, b) => a.order_index - b.order_index),
      tasks: st.tasks.filter((t) => t.project_id === pid),
      updates: st.updates.filter((u) => u.project_id === pid).sort((a, b) => b.created_at.localeCompare(a.created_at)),
      quoteSummary: (quote?.description ?? (quote as any)?.ai_payload?.summary ?? null) as string | null,
    });
  },

  // Operator hub — show all projects in sandbox demo
  async operatorAssignedProjects() {
    const st = s();
    return wait(st.projects.map((p) => {
      const milestones = st.milestones.filter((m) => m.project_id === p.id);
      const tasks = st.tasks.filter((t) => t.project_id === p.id);
      const updates = st.updates.filter((u) => u.project_id === p.id).map((u) => u.created_at).sort();
      const team = teamMembersFor(p.id);
      return {
        ...p,
        clients: p.client_id ? { company_name: companyFor(p.client_id) ?? "" } : null,
        team_count: Math.max(st.members.filter((m) => m.project_id === p.id).length, team.length),
        team_members: team,
        open_tasks: tasks.filter((t) => t.status !== "done" && t.status !== "cancelled").length,
        milestones_total: milestones.length,
        milestones_done: milestones.filter((m) => m.status === "completed" || m.status === "done").length,
        last_update: updates.length ? updates[updates.length - 1] : null,
      };
    }) as any);
  },

  async adminInsights() {
    const [clients, projects, invoices, quotes] = await Promise.all([
      mockApi.listClients(),
      mockApi.listProjects(),
      mockApi.listInvoices(),
      mockApi.listQuotes(),
    ]);
    return buildAdminInsights({ clients, projects, invoices, quotes });
  },

  /* ───────── Team profiles (mock) ───────── */
  async listTeamProfiles() {
    const st = s();
    return wait(st.teamProfiles.map((p) => ({
      ...p,
      projects_count: st.projectTeam.filter((pt) => pt.team_profile_id === p.id).length,
    })));
  },
  async getTeamProfile(pid) {
    return wait(s().teamProfiles.find((p) => p.id === pid) ?? null);
  },
  async createTeamProfile(p) {
    const newId = id();
    const newP: any = {
      id: newId,
      user_id: null,
      display_name: p.display_name ?? "",
      headline: p.headline ?? null,
      bio: p.bio ?? null,
      avatar_url: p.avatar_url ?? null,
      avatar_color: p.avatar_color ?? null,
      years_experience: p.years_experience ?? null,
      tenure_months: (p as any).tenure_months ?? null,
      birthday: (p as any).birthday ?? null,
      gender: (p as any).gender ?? null,
      location: p.location ?? null,
      email: p.email ?? null,
      languages: p.languages ?? [],
      expertise: p.expertise ?? [],
      certifications: p.certifications ?? [],
      experience: p.experience ?? [],
      education: p.education ?? [],
      linkedin_url: p.linkedin_url ?? null,
      website_url: p.website_url ?? null,
      is_active: p.is_active ?? true,
      resource_id: (p as any).resource_id ?? null,
      created_at: now(),
    };
    useMockStore.setState((st) => ({ teamProfiles: [newP, ...st.teamProfiles] }));
    return newId;
  },
  async updateTeamProfile(pid, patch) {
    useMockStore.setState((st) => ({
      teamProfiles: st.teamProfiles.map((p) => (p.id === pid ? { ...p, ...patch } : p)),
    }));
  },
  async deleteTeamProfile(pid) {
    useMockStore.setState((st) => ({
      teamProfiles: st.teamProfiles.filter((p) => p.id !== pid),
      projectTeam: st.projectTeam.filter((pt) => pt.team_profile_id !== pid),
    }));
  },
  async listProjectTeam(projectId) {
    const st = s();
    const rows = st.projectTeam.filter((pt) => pt.project_id === projectId).sort((a, b) => a.order_index - b.order_index);
    return wait(rows.map((r) => {
      const profile = st.teamProfiles.find((p) => p.id === r.team_profile_id);
      return profile ? { ...r, profile } : null;
    }).filter(Boolean) as any);
  },
  async assignTeamToProject(projectId, teamProfileId, roleOnProject) {
    useMockStore.setState((st) => {
      if (st.projectTeam.some((pt) => pt.project_id === projectId && pt.team_profile_id === teamProfileId)) return {};
      const order_index = st.projectTeam.filter((pt) => pt.project_id === projectId).length;
      // Inherit price tier (resource) and monthly rate from the team profile.
      const profile = st.teamProfiles.find((p) => p.id === teamProfileId);
      const resourceId = (profile as any)?.resource_id ?? null;
      const resource = resourceId ? st.resources.find((r) => r.id === resourceId) : null;
      // Honor the project's locked-in discount snapshot when present; only
      // fall back to the live global discount if the project predates the
      // snapshot column (defensive).
      const proj = st.projects.find((p) => p.id === projectId) as any;
      const pct = Number(
        proj?.resources_discount_percent_snapshot
          ?? (st.appSettings as any)?.resources_discount_percent
          ?? 0,
      );
      const mul = 1 - Math.max(0, Math.min(100, pct)) / 100;
      const monthly = resource ? Math.round(Number(resource.monthly_rate ?? 0) * mul * 100) / 100 : 0;
      return {
        projectTeam: [...st.projectTeam, {
          id: id(), project_id: projectId, team_profile_id: teamProfileId,
          role_on_project: roleOnProject ?? null, order_index,
          resource_id: resourceId,
          monthly_rate_snapshot: monthly,
        }],
      };
    });
  },
  async removeTeamFromProject(projectTeamId) {
    useMockStore.setState((st) => ({
      projectTeam: st.projectTeam.filter((pt) => pt.id !== projectTeamId),
    }));
  },
  async listProjectClientTeam(projectId) {
    const st = s();
    const project = st.projects.find((p) => p.id === projectId);
    const client = project ? st.clients.find((c) => c.id === project.client_id) : null;
    const seeded = mockProjectClientContacts[projectId];
    const overrides = st.clientContactOverrides;
    const applyOverride = (c: any) => {
      const o = overrides[c.id];
      if (!o) return c;
      return {
        ...c,
        display_name: o.display_name ?? c.display_name,
        email: o.email !== undefined ? o.email : c.email,
        location: o.location !== undefined ? o.location : c.location,
        is_primary: o.is_primary !== undefined ? o.is_primary : c.is_primary,
        tab_permissions: o.tab_permissions ?? c.tab_permissions ?? null,
        status: o.status ?? c.status ?? null,
      };
    };
    let contacts;
    if (seeded && seeded.length > 0) {
      contacts = seeded.map((c, i) => ({
        id: c.id ?? `${projectId}-cc-${i}`,
        display_name: c.display_name,
        role: c.role ?? null,
        email: c.email ?? null,
        phone: c.phone ?? null,
        avatar_url: c.avatar_url ?? null,
        location: c.location ?? null,
        is_primary: !!c.is_primary,
      }));
    } else if (client?.contact_name) {
      contacts = [{
        id: `primary-${projectId}`,
        display_name: client.contact_name,
        role: "Point of contact",
        email: client.contact_email ?? null,
        phone: client.contact_phone ?? null,
        avatar_url: null,
        location: null,
        is_primary: true,
      }];
    } else {
      contacts = [];
    }
    contacts = contacts
      .filter((c) => !overrides[c.id]?.removed)
      .map(applyOverride);
    const invited = st.projectInvitations
      .filter((i) => i.project_id === projectId)
      .map((i) => ({
        id: `inv-${i.id}`,
        invitation_id: i.id,
        display_name: i.name,
        role: null,
        email: i.email,
        phone: null,
        avatar_url: null,
        location: i.location,
        is_primary: i.is_primary,
        status: i.status,
        tab_permissions: i.tab_permissions,
        invited_at: i.invited_at,
        accepted_at: i.accepted_at,
        last_session_at: i.last_session_at,
      }));
    return wait({
      projectName: project?.name ?? "",
      clientName: client?.company_name ?? null,
      contacts: [...contacts, ...invited],
    });
  },

  /* ───────── Project invitations (mock) ───────── */
  async listProjectInvitations(projectId) {
    return wait(
      s().projectInvitations
        .filter((i) => i.project_id === projectId)
        .sort((a, b) => a.invited_at.localeCompare(b.invited_at)),
    );
  },
  async createProjectInvitation(projectId, p) {
    const newId = id();
    const row = {
      id: newId,
      project_id: projectId,
      name: p.name,
      email: p.email.trim().toLowerCase(),
      location: p.location ?? null,
      is_primary: !!p.is_primary,
      status: "pending" as const,
      tab_permissions: p.tab_permissions ?? { ...DEFAULT_TAB_PERMISSIONS },
      invited_at: now(),
      accepted_at: null,
      last_session_at: null,
    };
    useMockStore.setState((st) => ({ projectInvitations: [...st.projectInvitations, row] }));
    return newId;
  },
  async updateProjectInvitation(invId, patch) {
    const clean: any = { ...patch };
    if (typeof clean.email === "string") clean.email = clean.email.trim().toLowerCase();
    useMockStore.setState((st) => ({
      projectInvitations: st.projectInvitations.map((i) =>
        i.id === invId ? { ...i, ...clean } : i,
      ),
    }));
  },
  async deleteProjectInvitation(invId) {
    useMockStore.setState((st) => ({
      projectInvitations: st.projectInvitations.filter((i) => i.id !== invId),
    }));
  },
  async updateClientContact(contactId, patch) {
    useMockStore.setState((st) => ({
      clientContactOverrides: {
        ...st.clientContactOverrides,
        [contactId]: { ...(st.clientContactOverrides[contactId] ?? {}), ...patch },
      },
    }));
  },
  async deleteClientContact(contactId) {
    useMockStore.setState((st) => ({
      clientContactOverrides: {
        ...st.clientContactOverrides,
        [contactId]: { ...(st.clientContactOverrides[contactId] ?? {}), removed: true },
      },
    }));
  },

  /* ───────── Resources catalog (mock) ───────── */
  async listResources() {
    return wait([...s().resources].sort((a, b) => a.order_index - b.order_index));
  },
  async createResource(p) {
    const newId = id();
    const max = Math.min(10, Math.max(0, Number(p.max_overtime_hours ?? 10)));
    const newR: any = {
      id: newId,
      headline: p.headline ?? "",
      headline_es: p.headline_es ?? "",
      division: p.division ?? "operations",
      description_en: p.description_en ?? "",
      description_es: p.description_es ?? "",
      hourly_rate: Number(p.hourly_rate ?? 0),
      overtime_hourly_rate: Number(p.overtime_hourly_rate ?? 0),
      monthly_rate: Number(p.monthly_rate ?? 0),
      monthly_included_hours: Number(p.monthly_included_hours ?? 160),
      max_overtime_hours: max,
      currency: p.currency ?? "USD",
      is_active: p.is_active ?? true,
      order_index: Number(p.order_index ?? s().resources.length),
      created_at: now(),
    };
    useMockStore.setState((st) => ({ resources: [newR, ...st.resources] }));
    return newId;
  },
  async updateResource(rid, patch) {
    const clean: any = { ...patch };
    if (clean.max_overtime_hours != null) {
      clean.max_overtime_hours = Math.min(10, Math.max(0, Number(clean.max_overtime_hours)));
    }
    useMockStore.setState((st) => ({
      resources: st.resources.map((r) => (r.id === rid ? { ...r, ...clean } : r)),
    }));
  },
  async deleteResource(rid) {
    useMockStore.setState((st) => ({ resources: st.resources.filter((r) => r.id !== rid) }));
  },

  /* ───────── App settings (mock) ───────── */
  async getAppSettings() {
    return wait({ ...s().appSettings });
  },
  async updateAppSettings(patch) {
    useMockStore.setState((st) => ({
      appSettings: { ...st.appSettings, ...patch, updated_at: new Date().toISOString() },
    }));
  },

  /* ───────── Payment methods (mock) ───────── */
  async listPaymentMethods() {
    return wait([...s().paymentMethods].sort((a, b) => a.order_index - b.order_index));
  },
  async createPaymentMethod(p) {
    const newId = id();
    const newM: any = {
      id: newId,
      kind: p.kind ?? "bank",
      label: p.label ?? "",
      currency: p.currency ?? null,
      details: p.details ?? {},
      is_active: p.is_active ?? true,
      is_default_global: p.is_default_global ?? false,
      order_index: Number(p.order_index ?? s().paymentMethods.length),
      created_at: now(),
      payment_instructions_es: p.payment_instructions_es ?? "",
      payment_instructions_en: p.payment_instructions_en ?? "",
      footer_note_es: p.footer_note_es ?? "",
      footer_note_en: p.footer_note_en ?? "",
    };
    useMockStore.setState((st) => {
      const next = newM.is_default_global
        ? st.paymentMethods.map((m) => ({ ...m, is_default_global: false }))
        : st.paymentMethods;
      return { paymentMethods: [...next, newM] };
    });
    return newId;
  },
  async updatePaymentMethod(mid, patch) {
    useMockStore.setState((st) => {
      let list = st.paymentMethods;
      if (patch.is_default_global) {
        list = list.map((m) => ({ ...m, is_default_global: false }));
      }
      return { paymentMethods: list.map((m) => (m.id === mid ? { ...m, ...patch } as any : m)) };
    });
  },
  async deletePaymentMethod(mid) {
    useMockStore.setState((st) => ({
      paymentMethods: st.paymentMethods.filter((m) => m.id !== mid),
      clients: st.clients.map((c) =>
        (c as any).default_payment_method_id === mid ? { ...c, default_payment_method_id: null } : c,
      ),
      invoices: st.invoices.map((i) =>
        (i as any).payment_method_id === mid ? { ...i, payment_method_id: null } : i,
      ),
    }));
  },

  async createProjectFromQuote(quoteId) {
    const st = s();
    const q = st.quotes.find((x) => x.id === quoteId) as any;
    if (!q) throw new Error("Quote not found");
    if (q.project_id) throw new Error("Quote already has a project");
    const snap = Number(
      q.discount_snapshot?.percent
        ?? (st.appSettings as any)?.resources_discount_percent
        ?? 0,
    );
    const client = q.client_id ? st.clients.find((c) => c.id === q.client_id) : null;
    const lang: "es" | "en" = (client?.preferred_language === "en" ? "en" : "es");
    const {
      PHASE_NAMES, PHASE_DESCRIPTIONS, PHASE_WEEKS, PHASE_TASKS,
      lastDayOfMonthISO, proratedForCurrentMonth,
    } = await import("../projectFromQuoteTemplate");
    const newPid = id();
    const newProject: any = {
      id: newPid,
      name: q.title ?? "Project",
      description: null,
      client_id: q.client_id ?? null,
      status: "discovery",
      progress: 0,
      budget: q.total ?? null,
      currency: q.currency ?? "USD",
      start_date: now().slice(0, 10),
      resources_discount_percent_snapshot: snap,
      billing: {
        monthly_fee: q.monthly_fee ?? null,
        invoice_day_of_month: q.invoice_day_of_month ?? null,
        payment_due_day: q.payment_due_day ?? null,
        currency: q.currency ?? "USD",
      },
      created_at: now(),
    };
    newProject.clients = newProject.client_id ? { company_name: companyFor(newProject.client_id) ?? "" } : null;

    const mul = 1 - Math.max(0, Math.min(100, snap)) / 100;
    const teamRows = st.quoteTeam
      .filter((m) => m.quote_id === quoteId)
      .map((m, i) => {
        const profile = st.teamProfiles.find((p) => p.id === m.team_profile_id) as any;
        const rid = profile?.resource_id ?? null;
        const resource = rid ? st.resources.find((r) => r.id === rid) : null;
        const monthly = resource ? Math.round(Number(resource.monthly_rate ?? 0) * mul * 100) / 100 : 0;
        return {
          id: id(),
          project_id: newPid,
          team_profile_id: m.team_profile_id,
          role_on_project: m.role_on_project ?? null,
          order_index: i,
          resource_id: rid,
          monthly_rate_snapshot: monthly,
        };
      });

    // Per-tier custom phase weeks (if the wizard saved them), else defaults.
    const tier: string = q.selected_budget ?? "balanced";
    const customWeeks: number[] | null = (() => {
      const w = q.ai_payload?.phase_weeks_by_tier?.[tier];
      return Array.isArray(w) && w.length === PHASE_WEEKS.length
        ? w.map((v: any, i: number) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.round(Number(v)) : PHASE_WEEKS[i]))
        : null;
    })();
    const weeks = customWeeks ?? PHASE_WEEKS;

    // Build 10 milestones with cumulative due_dates from project start.
    const startMs = new Date(newProject.start_date).getTime();
    let cum = 0;
    const milestones: any[] = PHASE_NAMES[lang].map((name, i) => {
      cum += weeks[i] ?? 2;
      const due = new Date(startMs + cum * 7 * 86400000).toISOString().slice(0, 10);
      return {
        id: id(),
        project_id: newPid,
        title: name,
        description: PHASE_DESCRIPTIONS[lang][i] ?? null,
        status: "pending",
        order_index: i,
        due_date: due,
        created_at: now(),
      };
    });

    // Seed tasks for the first two milestones, round-robin across the team.
    const assignees = teamRows.map((tr) => {
      const tp = st.teamProfiles.find((p) => p.id === tr.team_profile_id) as any;
      return tp?.user_id ?? tp?.id ?? null;
    }).filter(Boolean) as string[];
    const tasks: any[] = [];
    PHASE_TASKS[lang].forEach((phaseTasks, phaseIdx) => {
      const ms = milestones[phaseIdx];
      const phaseStartWeeks = weeks.slice(0, phaseIdx).reduce((a, b) => a + b, 0);
      const phaseLen = weeks[phaseIdx] ?? 2;
      phaseTasks.forEach((t, ti) => {
        const offsetDays = Math.round((phaseStartWeeks * 7) + ((ti + 1) / (phaseTasks.length + 1)) * phaseLen * 7);
        tasks.push({
          id: id(),
          project_id: newPid,
          milestone_id: ms.id,
          title: t.title,
          description: t.description,
          status: "pending",
          assignee_id: assignees.length ? assignees[(ti) % assignees.length] : null,
          due_date: new Date(startMs + offsetDays * 86400000).toISOString().slice(0, 10),
          created_at: now(),
        });
      });
    });

    // First prorated invoice — due on the last day of the current month.
    const monthly = Number(q.monthly_fee ?? 0);
    const invoice: any | null = monthly > 0 ? (() => {
      const { amount } = proratedForCurrentMonth(monthly);
      const issue = now().slice(0, 10);
      const due = lastDayOfMonthISO();
      const note = lang === "es"
        ? `Factura prorrateada — primer mes (${issue} al ${due}). Generada al crear el proyecto desde la cotización ${q.quote_number ?? ""}.`.trim()
        : `Prorated invoice — first month (${issue} to ${due}). Generated when the project was created from quote ${q.quote_number ?? ""}.`.trim();
      return {
        id: id(),
        client_id: q.client_id ?? null,
        invoice_number: `INV-${issue.replace(/-/g, "")}-${(q.quote_number ?? "").slice(-4) || "0001"}`,
        amount,
        currency: q.currency ?? "USD",
        status: "sent",
        issue_date: issue,
        due_date: due,
        paid_at: null,
        created_at: now(),
        notes: note,
        clients: q.client_id ? { company_name: companyFor(q.client_id) ?? "" } : null,
        reminder_cadence: "weekly",
      };
    })() : null;

    useMockStore.setState((cur) => ({
      projects: [newProject, ...cur.projects],
      projectTeam: [...cur.projectTeam, ...teamRows],
      milestones: [...cur.milestones, ...milestones],
      tasks: [...cur.tasks, ...tasks],
      invoices: invoice ? [invoice, ...cur.invoices] : cur.invoices,
      quotes: cur.quotes.map((qq) => (qq.id === quoteId ? ({ ...qq, project_id: newPid } as any) : qq)),
    }));

    return newPid;
  },
};